<script lang="ts">
	import { page } from '$app/stores';
	import { translations } from '$lib/i18n/translations';
	import type { Article, Property } from '$lib/satellite.types';
	import { onMount } from 'svelte';

	export let article: Article;
	export let property: Property;
	export let canonical: string;

	const t = translations($page.data.property.language);

	let canShare = false;
	let showAppleNews = false;
	let fromPinterest = false;

	const share = async () => {
		try {
			await navigator.share({
				title: article.title,
				text: article.description,
				url: canonical
			});
		} catch (error) {
			// Too bad
		}
	};

	const getPinterestUri = () => {
		const uri = new URL('https://www.pinterest.com/pin/create/button/');
		uri.searchParams.append('url', canonical);
		uri.searchParams.append('media', article.imageSocial.url);
		uri.searchParams.append('description', article.title);
		return uri.toString();
	};

	onMount(() => {
		// Check if device is Apple
		const isApple = /Mac|iPhone|iPad/.test(navigator.userAgent);
		showAppleNews = isApple && property?.appleNews !== undefined;

		// Check if device can share
		canShare = navigator?.share !== undefined;

		// Check if referrer is Pinterest
		if (document.referrer.includes('pinterest')) {
			fromPinterest = true;
		}
	});
</script>

<div
	class="flex justify-end lg:justify-start bg-gray-100 mt-0.5 rounded-lg overflow-hidden print:hidden"
>
	{#if canShare}
		<button
			on:click={share}
			class="bg-indigo-600 hover:bg-indigo-700 text-white p-3 px-4 text-sm font-semibold flex items-center space-x-2"
		>
			<svg
				xmlns="http://www.w3.org/2000/svg"
				fill="currentColor"
				class="w-4 h-4 inline"
				viewBox="0 0 24 24"
				><title>{t('share')}</title><path
					fill-rule="evenodd"
					d="M11.47 2.47a.75.75 0 0 1 1.06 0l4.5 4.5a.75.75 0 0 1-1.06 1.06l-3.22-3.22V16.5a.75.75 0 0 1-1.5 0V4.81L8.03 8.03a.75.75 0 0 1-1.06-1.06l4.5-4.5ZM3 15.75a.75.75 0 0 1 .75.75v2.25a1.5 1.5 0 0 0 1.5 1.5h13.5a1.5 1.5 0 0 0 1.5-1.5V16.5a.75.75 0 0 1 1.5 0v2.25a3 3 0 0 1-3 3H5.25a3 3 0 0 1-3-3V16.5a.75.75 0 0 1 .75-.75Z"
					clip-rule="evenodd"
				/></svg
			>
			<span class="hidden lg:inline whitespace-nowrap text-clip">{t('share')}</span>
		</button>
	{/if}

	{#if showAppleNews}
		<a
			href={property.appleNews}
			target="_blank"
			class="bg-[#FD415E] hover:bg-[#c30220] text-white p-3 px-4 text-sm font-semibold flex items-center space-x-2"
		>
			<svg
				xmlns="http://www.w3.org/2000/svg"
				role="img"
				class="h-4 w-4 text-white inline"
				fill="currentColor"
				viewBox="0 0 24 24"
				><title>Apple News</title><path
					d="M0 12.94c2.726 4.673 6.394 8.385 11.039 11.058H1.416A1.416 1.416 0 0 1 0 22.582V12.94ZM0 1.414C0 .634.632.002 1.412.002H6.22L24 17.758v4.773c0 .39-.154.762-.43 1.038a1.467 1.467 0 0 1-1.037.43H17.76L0 6.241M12.963.002h9.619A1.419 1.419 0 0 1 24 1.42v9.625C21.265 6.494 17.616 2.775 12.963.002Z"
				/></svg
			>
			<span class="hidden lg:inline whitespace-nowrap text-clip">{t('followUsOn')} Apple News</span>
		</a>
	{/if}

	{#if property?.googleNews}
		<a
			href={property.googleNews}
			target="_blank"
			class="bg-[#174EA6] hover:bg-[#1a498e] text-white p-3 px-4 text-sm font-semibold flex items-center space-x-2"
		>
			<svg
				role="img"
				class="h-4 w-4 text-white inline"
				fill="currentColor"
				viewBox="0 0 24 24"
				xmlns="http://www.w3.org/2000/svg"
				><title>Google News</title><path
					d="M21.2666 21.2a.6141.6141 0 0 1-.6128.6128H3.3446a.614.614 0 0 1-.6128-.6128V8.1153a.6141.6141 0 0 1 .6128-.6128h17.3091a.6141.6141 0 0 1 .6128.6128V21.2zm-3.0315-3.4196v-1.1957a.0803.0803 0 0 0-.0803-.0803h-5.3725v1.3619h5.3725a.0817.0817 0 0 0 .0804-.083l-.0001-.0029zm.8171-2.5875v-1.2012a.0803.0803 0 0 0-.079-.0817h-6.191v1.3619h6.1896a.079.079 0 0 0 .0804-.0776v-.0041.0027zm-.8171-2.5875v-1.2066a.0803.0803 0 0 0-.0803-.0803h-5.3725v1.3619h5.3725a.0816.0816 0 0 0 .0803-.079v.004zM8.1492 14.0448v1.2257h1.7704c-.1457.749-.8049 1.2924-1.7704 1.2924-1.0906-.0405-1.9418-.9574-1.9013-2.048.0384-1.0338.8676-1.8629 1.9013-1.9013a1.77 1.77 0 0 1 1.2529.4903l.9342-.9329a3.1405 3.1405 0 0 0-2.1871-.8525c-1.8051 0-3.2684 1.4633-3.2684 3.2684 0 1.8051 1.4633 3.2684 3.2684 3.2684 1.8889 0 3.1323-1.3278 3.1323-3.1976a3.941 3.941 0 0 0-.0518-.6183l-3.0805.0054zM2.2701 7.6537a.6156.6156 0 0 1 .6128-.6128h12.1545l-1.2692-3.4904a.5952.5952 0 0 0-.7436-.3827L.3676 7.7749a.5938.5938 0 0 0-.3228.7749l2.2253 6.112V7.6537za.6156.6156 0 0 1 .6128-.6128h12.1545l-1.2692-3.4904a.5952.5952 0 0 0-.7436-.3827L.3676 7.7749a.5938.5938 0 0 0-.3228.7749l2.2253 6.112V7.6537zm21.3116-.3105l-8.803-2.3683.7517 2.0659h5.5836a.6141.6141 0 0 1 .6128.6128v8.7948l2.2471-8.3659a.5923.5923 0 0 0-.3922-.7393zm-4.4955-1.6738V2.7946a.6101.6101 0 0 0-.6115-.6074H5.5236a.6101.6101 0 0 0-.6156.6046v2.8368l8.3904-3.0519a.5937.5937 0 0 1 .7422.3895l.5447 1.498 4.5009 1.2052z"
				/></svg
			>
			<span class="hidden lg:inline whitespace-nowrap text-clip">{t('followUsOn')} Google News</span
			>
		</a>
	{/if}

	{#if property?.enablePrint}
		<button
			on:click={() => {
				window.print();
			}}
			class="bg-teal-500 hover:bg-teal-600 text-white p-3 px-4 text-sm font-semibold flex items-center space-x-2"
		>
			<svg
				xmlns="http://www.w3.org/2000/svg"
				fill="currentColor"
				class="w-4 h-4 text-white inline"
				viewBox="0 0 24 24"
				><path
					fill-rule="evenodd"
					d="M7.875 1.5C6.839 1.5 6 2.34 6 3.375v2.99c-.426.053-.851.11-1.274.174-1.454.218-2.476 1.483-2.476 2.917v6.294a3 3 0 0 0 3 3h.27l-.155 1.705A1.875 1.875 0 0 0 7.232 22.5h9.536a1.875 1.875 0 0 0 1.867-2.045l-.155-1.705h.27a3 3 0 0 0 3-3V9.456c0-1.434-1.022-2.7-2.476-2.917A48.716 48.716 0 0 0 18 6.366V3.375c0-1.036-.84-1.875-1.875-1.875h-8.25ZM16.5 6.205v-2.83A.375.375 0 0 0 16.125 3h-8.25a.375.375 0 0 0-.375.375v2.83a49.353 49.353 0 0 1 9 0Zm-.217 8.265c.178.018.317.16.333.337l.526 5.784a.375.375 0 0 1-.374.409H7.232a.375.375 0 0 1-.374-.409l.526-5.784a.373.373 0 0 1 .333-.337 41.741 41.741 0 0 1 8.566 0Zm.967-3.97a.75.75 0 0 1 .75-.75h.008a.75.75 0 0 1 .75.75v.008a.75.75 0 0 1-.75.75H18a.75.75 0 0 1-.75-.75V10.5ZM15 9.75a.75.75 0 0 0-.75.75v.008c0 .414.336.75.75.75h.008a.75.75 0 0 0 .75-.75V10.5a.75.75 0 0 0-.75-.75H15Z"
					clip-rule="evenodd"
				/></svg
			>
			<span class="hidden lg:inline whitespace-nowrap text-clip">Imprimer</span>
		</button>
	{/if}

	{#if property?.enablePinterest}
		<a
			href={getPinterestUri()}
			target="_blank"
			rel="noopener noreferrer"
			class="bg-[#BD081C] hover:bg-[#AA0719] text-white p-3 px-4 text-sm font-semibold flex items-center space-x-2"
			class:order-last={fromPinterest}
		>
			<svg
				role="img"
				class="h-4 w-4 text-white inline"
				viewBox="0 0 24 24"
				fill="currentColor"
				xmlns="http://www.w3.org/2000/svg"
				><title>Pinterest</title><path
					d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.162-.105-.949-.199-2.403.041-3.439.219-.937 1.406-5.957 1.406-5.957s-.359-.72-.359-1.781c0-1.663.967-2.911 2.168-2.911 1.024 0 1.518.769 1.518 1.688 0 1.029-.653 2.567-.992 3.992-.285 1.193.6 2.165 1.775 2.165 2.128 0 3.768-2.245 3.768-5.487 0-2.861-2.063-4.869-5.008-4.869-3.41 0-5.409 2.562-5.409 5.199 0 1.033.394 2.143.889 2.741.099.12.112.225.085.345-.09.375-.293 1.199-.334 1.363-.053.225-.172.271-.401.165-1.495-.69-2.433-2.878-2.433-4.646 0-3.776 2.748-7.252 7.92-7.252 4.158 0 7.392 2.967 7.392 6.923 0 4.135-2.607 7.462-6.233 7.462-1.214 0-2.354-.629-2.758-1.379l-.749 2.848c-.269 1.045-1.004 2.352-1.498 3.146 1.123.345 2.306.535 3.55.535 6.607 0 11.985-5.365 11.985-11.987C23.97 5.39 18.592.026 11.985.026L12.017 0z"
				/></svg
			>
			<span
				class="whitespace-nowrap text-clip"
				class:hidden={!fromPinterest}
				class:lg:inline={!fromPinterest}
				>{t('pin')}
				<span class:hidden={fromPinterest} class:lg:inline={fromPinterest}>{t('on')} Pinterest</span
				></span
			>
		</a>
	{/if}

	<a
		href="https://www.facebook.com/sharer/sharer.php?u={canonical}"
		target="_blank"
		rel="noopener noreferrer"
		class="bg-[#0866FF] hover:bg-[#0a54eb] text-white p-3 px-4 text-sm font-semibold flex items-center space-x-2"
	>
		<svg
			role="img"
			class="h-4 w-4 inline text-white"
			viewBox="0 0 24 24"
			xmlns="http://www.w3.org/2000/svg"
			fill="currentColor"
			><title>Facebook</title><path
				d="M9.101 23.691v-7.98H6.627v-3.667h2.474v-1.58c0-4.085 1.848-5.978 5.858-5.978.401 0 .955.042 1.468.103a8.68 8.68 0 0 1 1.141.195v3.325a8.623 8.623 0 0 0-.653-.036 26.805 26.805 0 0 0-.733-.009c-.707 0-1.259.096-1.675.309a1.686 1.686 0 0 0-.679.622c-.258.42-.374.995-.374 1.752v1.297h3.919l-.386 2.103-.287 1.564h-3.246v8.245C19.396 23.238 24 18.179 24 12.044c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.628 3.874 10.35 9.101 11.647Z"
			/></svg
		>
		<span
			class="whitespace-nowrap text-clip"
			class:hidden={fromPinterest}
			class:lg:inline={fromPinterest}
			>{t('share')} <span class="hidden lg:inline">{t('on')} Facebook</span></span
		>
	</a>

	{#if property?.enableFbLike}
		<div class="flex items-center px-3 bg-gray-200">
			<div class="pulse rounded-sm bg-[#abc7ff]">
				<iframe
					title="Facebook Like"
					src="https://www.facebook.com/plugins/like.php?href={encodeURIComponent(
						canonical
					)}&width=100&layout=button_count&action=like&size=large&share=false&height=28&appId={property.fbApp}"
					width="100"
					height="28"
					style="border:none;overflow:hidden"
					scrolling="no"
					frameborder="0"
					allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
				></iframe>
			</div>
		</div>
	{/if}
</div>

<style>
	/*
	Disabled since it loads an additional CSS for nothing, and we're not using FB Like button right now
		.pulse {
			box-shadow: 0 0 0 0 rgba(52, 172, 224, 1);
			animation: pulse 2s infinite;
		}

		@keyframes pulse {
			0% {
				box-shadow: 0 0 0 0 rgba(52, 172, 224, 0.7);
			}

			70% {
				box-shadow: 0 0 0 10px rgba(52, 172, 224, 0);
			}

			100% {
				box-shadow: 0 0 0 0 rgba(52, 172, 224, 0);
			}
		}
	*/
</style>
